import React, { useCallback, useState, useEffect, FC } from 'react';
import { useDispatch } from 'react-redux';
import NAV_DATA, {
  SecondaryNavItem,
  SecondaryNavIdentifier,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import type { UserContext } from '@zola-helpers/client/dist/es/@types';
import _isEmpty from 'lodash/isEmpty';
import cx from 'classnames';

// import PromotionalBar from 'components/navV2/PromotionalBar';
import NavPrimary from 'components/navV2/NavPrimary';
import NavSecondary from 'components/navV2/NavSecondary';
import NavScrollContainer from 'components/navV2/NavScrollContainer';
import NavUtil from 'components/navV2/NavUtil';

import { notDesktop } from '@zola-helpers/client/dist/es/util/responsive';
import useNavV1Methods from 'components/navV2/useNavV1Methods';
import useNavData from 'components/navV2/useNavData';
import useSetWebZolaNavStyles from 'components/navV3/PostAuth/useSetWebZolaNavStyles';
import { UniversalTabs } from 'reducers/nav';
import type { WStoreNavView } from '@zola/svc-web-api-ts-client';
import { setActiveUniversalTab } from 'actions/NavActions';
import { isPostAuthNav3Desktop } from 'components/navV3/PostAuth/utils';

import homeStorePrimaryNav from './isHomeStore';
import FeatureFlags from '../../util/featureFlags';
import MultiAnnouncementBanner from '../navV3/MultiAnnouncementBanner/MultiAnnouncementBanner';

import './navV2.less';
import type { NavV2MapDispatchProps, NavV2MapStateProps } from '.';
import { isLoggedIn } from '../../util/authHelpers';

const PublicRegistryNavContainer = React.lazy(
  () => import(/* webpackChunkName: "public-registry-nav" */ 'components/publicRegistryNav')
);

const PublicRegistryNavV2 = React.lazy(
  () => import(/* webpackChunkName: "public-registry-nav" */ 'components/publicRegistryNav/v2')
);

const PostAuthDesktopNavV3 = React.lazy(
  () =>
    import(
      /* webpackChunkName: "post-auth-v3-nav" */ 'components/navV3/PostAuth/PostAuthDesktopNavV3/PostAuthDesktopNavV3'
    )
);

const {
  EVENTS: { PUBLIC_REGISTRY_EVENT },
} = NAV_DATA;

// event data to be received from initNavData for setting home store tab as default when loading

type NavV2Props = NavV2MapStateProps & NavV2MapDispatchProps;

const NavV2: FC<NavV2Props> = ({
  addUserContextToRedux,
  maybeFetchUser,
  fetchCategories,
  fetchWeddingShopCategories,
  userContext,
  activeUniversalTab,
}) => {
  const [isPublicRegistry, setIsPublicRegistry] = useState(false);
  const [isPublicRegistryV2, setIsPublicRegistryV2] = useState(false); // TODO: to be removed when the new UI is released
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentTab, setCurrentTab] = useState('');
  const [homeStoreNav, setHomeStoreNav] = useState<WStoreNavView>({ categories: [] });
  const dispatch = useDispatch();
  const userContextExists = userContext && !_isEmpty(userContext);

  useEffect(() => {
    const handleSetPublicRegistry = (event: Event) => {
      if ((event as CustomEvent)?.detail === 'EXIT') {
        setIsPublicRegistry(false);
        setIsPublicRegistryV2(false);
      }
      if ((event as CustomEvent)?.detail === 'ENTER_V2') {
        setIsPublicRegistryV2(true);
      } else {
        setIsPublicRegistry(true);
      }
    };
    window.addEventListener(PUBLIC_REGISTRY_EVENT, handleSetPublicRegistry);
    return function cleanup() {
      window.removeEventListener(PUBLIC_REGISTRY_EVENT, handleSetPublicRegistry);
    };
  }, [isPublicRegistry, isPublicRegistryV2, setIsPublicRegistry, setIsPublicRegistryV2]);

  // useCallback so the referential check in useNavData's useEffect works as expected
  const onInitNavData = useCallback(
    (passedUserContext, defaultTab) => {
      if (defaultTab === 'HOME_STORE') {
        setCurrentTab('HOME_STORE');
        dispatch(setActiveUniversalTab(UniversalTabs.HOME_STORE));
      }
      if (!_isEmpty(passedUserContext)) addUserContextToRedux(passedUserContext);
    },
    [addUserContextToRedux, dispatch]
  );

  const {
    hideAnnouncementBanner,
    // promotionalBarData,
    // accountNavData,
    primaryNavData,
    secondaryNavData,
    primaryLinkId,
    secondaryLinkId,
    tertiaryLinkId,
    disablePrimaryCollapse,
    disablePromoCollapse,
    emitDirectionEvent,
    tertiaryNavData,
    quickLinksData,
  } = useNavData(onInitNavData, userContext, activeUniversalTab);

  // Extracted hook for V1 support
  useNavV1Methods({
    maybeFetchUser,
    fetchCategories,
    fetchWeddingShopCategories,
  });

  useSetWebZolaNavStyles({
    primaryLinkId,
    secondaryNavData,
    tertiaryNavData,
    isDesktop: true,
    isPostAuthNav3: userContextExists && isPostAuthNav3Desktop(userContext),
  });

  const isHomeStoreActive = activeUniversalTab === UniversalTabs.HOME_STORE;
  const navContainerClasses = cx('navV2__container', {
    'nav-container--blue-background': isHomeStoreActive,
  });

  // ticket REGISTRY-4769 is asking for changes to what is returned from api call for holidays only
  const changeRegistryHolidayCollections = () => {
    const holidayCollectionLinks: SecondaryNavItem[] = [
      {
        title: 'Registry Essentials',
        path: '/shop/collection/zola-registry-essentials',
        position: 1,
      },
      {
        title: 'Ultimate Kitchen Essentials',
        path: '/shop/collection/top-kitchen-essentials',
        position: 2,
      },
      { title: 'Entertaining Must-Haves', path: '/shop/collection/easy-entertaining', position: 3 },
      { title: '2021 Most wanted Gifts', path: '/shop/collection/most-wanted', position: 4 },
      { title: 'Gifts Under $100', path: '/shop/collection/gifts-under-1002', position: 5 },
      { title: 'Gifts Under $200', path: '/shop/collection/gifts-under-200', position: 6 },
      {
        title: 'Gifts for the Person Who Has Everything',
        path: '/shop/collection/gifts-for-adventurers',
        position: 7,
      },
    ];

    if (secondaryNavData && secondaryNavData.length) {
      return secondaryNavData.map((category) => {
        if (category?.identifier === SecondaryNavIdentifier.COLLECTIONS) {
          // eslint-disable-next-line no-param-reassign
          category.children = holidayCollectionLinks;
        }
        return category;
      });
    }
    return [];
  };

  // the dropdowns for NavPrimary and NavSecondary look different
  // the most efficient and minimal changes for home store nav:
  // hide primary nav
  // add carets to secondary nav
  // just style the css so secondary nav looks like primary nav
  const returnSecondaryNavLinks = (showHomeStoreLinks: boolean) => {
    if (showHomeStoreLinks && homeStoreNav?.categories) {
      return homeStoreNav.categories.length > 0
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          homeStorePrimaryNav(homeStoreNav as any, userContext as UserContext)
        : [];
    }
    // eslint-disable-next-line consistent-return
    return FeatureFlags.get('showHolidayCollectionLinks')
      ? changeRegistryHolidayCollections()
      : secondaryNavData;
  };

  if (!userContextExists) {
    maybeFetchUser();
  }

  useEffect(() => {
    if (isHomeStoreActive && homeStoreNav?.categories?.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchCategories('STORE').then(setHomeStoreNav);
    }
  }, [fetchCategories, homeStoreNav, isHomeStoreActive, setHomeStoreNav]);

  if (!userContextExists || notDesktop()) return <></>;
  if (isPublicRegistry) return <PublicRegistryNavContainer />;
  if (FeatureFlags.get('registryNewPublicStandalone') && isPublicRegistryV2)
    return <PublicRegistryNavV2 />;

  if (!isHomeStoreActive && isPostAuthNav3Desktop(userContext)) {
    return (
      <PostAuthDesktopNavV3
        userContext={userContext}
        secondaryNavData={secondaryNavData}
        activeLinkId={primaryLinkId}
        activeSecondaryLink={secondaryLinkId}
        activeTertiaryLink={tertiaryLinkId}
        activeUniversalTab={activeUniversalTab}
        hideAnnouncementBanner={hideAnnouncementBanner}
        isHomeStoreActive={isHomeStoreActive}
        tertiaryNavData={tertiaryNavData}
        quickLinksData={quickLinksData}
      />
    );
  }

  const showMultiAnnouncementBanner = isHomeStoreActive;

  return (
    <NavScrollContainer
      activeLinkId={primaryLinkId}
      disablePrimaryCollapse={disablePrimaryCollapse}
      disablePromoCollapse={disablePromoCollapse}
      emitDirectionEvent={emitDirectionEvent}
    >
      <nav className={navContainerClasses}>
        {showMultiAnnouncementBanner && (
          <MultiAnnouncementBanner
            showHomestoreLinks={isHomeStoreActive}
            leftAlignBanners={isHomeStoreActive}
            isPostAuth={isLoggedIn(userContext)}
          />
        )}
        {/* <PromotionalBar
          promotionalBarData={promotionalBarData.children}
          accountNavData={accountNavData}
          activeUniversalTab={activeUniversalTab}
          currentTab={currentTab}
          activeLinkId={primaryLinkId}
        /> */}
        <NavUtil activeUniversalTab={activeUniversalTab} isHomeStoreActive={isHomeStoreActive} />
        {!isHomeStoreActive && !isLoggedIn(userContext) && (
          <NavPrimary
            primaryNavData={primaryNavData.children}
            activeLinkId={primaryLinkId}
            activeUniversalTab={activeUniversalTab}
          />
        )}
        <NavSecondary
          secondaryNavData={returnSecondaryNavLinks(isHomeStoreActive)}
          activeLinkId={secondaryLinkId}
        />
      </nav>
    </NavScrollContainer>
  );
};

export default NavV2;
